import { useState } from "react";
import { Box, Flex, Text, Button, Stack, HStack, Icon, Input } from "@chakra-ui/react";
import { ChevronDown, ChevronUp, HardHat, Trash2, PenLine } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";
import { colors } from "@/theme/colors";

const inp = { h: "44px", bg: colors.white, borderColor: colors.border, borderRadius: "10px", fontSize: "sm" };

// Collapsible "Contractor Sign-Off" box shown at the bottom of a form while
// it's being completed. Collapsed by default. Multiple contractors may sign.
export const ContractorSignoffBox = ({ contractors, signoffs, onChange }) => {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  return (
    <Box border="1px solid" borderColor={colors.border} borderRadius="12px" bg={colors.white} data-testid="contractor-signoff-box">
      <Flex as="button" type="button" w="full" align="center" gap="2.5" px="4" py="3.5"
        onClick={() => setOpen((v) => !v)} data-testid="signoff-toggle">
        <Icon as={HardHat} boxSize="5" color={colors.teal} />
        <Text flex="1" textAlign="left" fontWeight="600" fontSize="sm" color={colors.textPrimary}>
          Contractor Sign-Off{signoffs.length > 0 ? ` (${signoffs.length})` : ""}
        </Text>
        <Icon as={open ? ChevronUp : ChevronDown} boxSize="4" color={colors.textSecondary} />
      </Flex>

      {open && (
        <Box px="4" pb="4" borderTop="1px solid" borderColor={colors.border} pt="3">
          <Text fontSize="xs" color={colors.textSecondary} mb="3">
            Have a contractor on site sign this form. Their name, signature and timestamp become part of the permanent record.
          </Text>

          {signoffs.length > 0 && (
            <Stack gap="2" mb="3">
              {signoffs.map((s) => (
                <SignoffRow key={s.id} s={s} onRemove={() => onChange(signoffs.filter((x) => x.id !== s.id))} />
              ))}
            </Stack>
          )}

          {!adding ? (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="signoff-add-btn">
              <PenLine size={14} /> Add contractor sign-off
            </Button>
          ) : (
            <SignoffForm contractors={contractors}
              onAdd={(entry) => { onChange([...signoffs, entry]); setAdding(false); }}
              onCancel={() => setAdding(false)} />
          )}
        </Box>
      )}
    </Box>
  );
};

const SignoffRow = ({ s, onRemove }) => (
  <Flex align="center" gap="3" px="3" py="2" borderRadius="10px" bg={colors.cream} data-testid={`signoff-row-${s.id}`}>
    <img src={s.dataUrl} alt="signature" style={{ width: 64, height: 30, objectFit: "contain", background: "#fff", borderRadius: 6, border: `1px solid ${colors.border}` }} />
    <Box flex="1" minW="0">
      <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{s.name}</Text>
      <Text fontSize="xs" color={colors.textSecondary}>
        {s.company && s.company !== s.name ? `${s.company} · ` : ""}{new Date(s.signed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </Text>
    </Box>
    <Button size="xs" variant="ghost" color={colors.poppy} onClick={onRemove}
      data-testid={`signoff-remove-${s.id}`} aria-label="Remove"><Trash2 size={14} /></Button>
  </Flex>
);

const SignoffForm = ({ contractors, onAdd, onCancel }) => {
  const [pick, setPick] = useState("");
  const [free, setFree] = useState({ name: "", company: "", contact: "" });
  const [sig, setSig] = useState(null);
  const [err, setErr] = useState(null);
  const notListed = pick === "__other";

  const add = () => {
    const picked = contractors.find((c) => c.id === pick);
    const name = notListed ? free.name.trim() : picked?.name;
    if (!name) { setErr("Choose a contractor or enter their name."); return; }
    if (!sig) { setErr("A signature is required."); return; }
    onAdd({
      id: crypto.randomUUID(),
      contractor_id: picked?.id || null,
      name,
      company: notListed ? free.company.trim() || null : picked?.name || null,
      contact: notListed ? free.contact.trim() || null : null,
      dataUrl: sig,
      signed_at: new Date().toISOString(),
    });
  };

  return (
    <Stack gap="3">
      <Box as="select" value={pick} onChange={(e) => { setPick(e.target.value); setErr(null); }}
        data-testid="signoff-contractor-select" w="full" px="3" h="44px" borderRadius="10px"
        border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm">
        <option value="">Choose a contractor…</option>
        {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}{c.trade ? ` · ${c.trade}` : ""}</option>)}
        <option value="__other">Contractor not listed — enter manually</option>
      </Box>
      {notListed && (
        <Stack gap="2">
          <Input {...inp} placeholder="Contractor name *" value={free.name}
            onChange={(e) => setFree({ ...free, name: e.target.value })} data-testid="signoff-free-name" />
          <Input {...inp} placeholder="Company" value={free.company}
            onChange={(e) => setFree({ ...free, company: e.target.value })} data-testid="signoff-free-company" />
          <Input {...inp} placeholder="Email or phone" value={free.contact}
            onChange={(e) => setFree({ ...free, contact: e.target.value })} data-testid="signoff-free-contact" />
        </Stack>
      )}
      {(pick && pick !== "") && (
        <Box>
          <Text fontSize="xs" fontWeight="600" color={colors.textPrimary} mb="1.5">Contractor signature *</Text>
          <SignaturePad value={sig} onChange={setSig} testId="signoff-signature" />
        </Box>
      )}
      {err && <Text fontSize="sm" color={colors.criticalText} data-testid="signoff-error">{err}</Text>}
      <HStack gap="2">
        <Button size="sm" onClick={add} data-testid="signoff-save-btn">Add sign-off</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="signoff-cancel-btn">Cancel</Button>
      </HStack>
    </Stack>
  );
};
